import { ImageOff, Trash2, Undo2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { Button } from "components/ui/button";
import { FileInput } from "components/ui/file-input";

import { ENTITY_IMAGE_TYPES, entityImageProblem } from "../entity-images";

export type ImageShape = "logo" | "portrait";

/**
 * The picture on a content document, chosen from a file.
 *
 * Nothing is uploaded here. The chosen file waits in the form until Save, so
 * cancelling the dialog leaves nothing in the bucket, and the upload and the
 * document write happen together in EntityCrudSection. The preview is a
 * local object URL until then.
 *
 * `value` is the URL on the document, which may be a file uploaded earlier, a
 * URL pasted before uploads existed, or a roster avatar a speaker link copied
 * in. All three show and all three survive an edit that does not touch them.
 */
export function EntityImageField({
  id,
  testId,
  value,
  file,
  required,
  shape,
  maxBytes,
  disabled,
  onFile,
  onRemove,
  onUndo,
  undoLabel,
}: {
  id: string;
  testId: string;
  value: string;
  file: File | undefined;
  required: boolean;
  shape: ImageShape;
  maxBytes: number;
  disabled: boolean;
  onFile: (file: File) => void;
  onRemove: () => void;
  onUndo: () => void;
  /** Set when the form differs from what the document holds, naming the way
   *  back; absent when there is nothing to undo. */
  undoLabel?: string;
}) {
  const [problem, setProblem] = useState<string | null>(null);
  const [broken, setBroken] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  // The native input names the file it holds, so it is emptied only when the
  // form no longer holds one (Remove, Keep the current image). Emptying it on
  // every pick left it reading "No file chosen" beside the preview of the
  // file just chosen.
  useEffect(() => {
    if (!file && input.current) input.current.value = "";
  }, [file]);

  const localUrl = useMemo(
    () =>
      file && typeof URL.createObjectURL === "function"
        ? URL.createObjectURL(file)
        : null,
    [file],
  );
  useEffect(
    () => () => {
      if (localUrl) URL.revokeObjectURL(localUrl);
    },
    [localUrl],
  );

  const src = localUrl ?? (value || null);
  useEffect(() => setBroken(false), [src]);

  const frame =
    shape === "portrait"
      ? "h-20 w-20 rounded-full object-cover"
      : "h-20 w-full max-w-[12rem] rounded-md object-contain bg-muted p-2";

  return (
    <div className="space-y-2" data-testid={`${testId}-field`}>
      {src && !broken ? (
        <img
          src={src}
          alt=""
          className={frame}
          data-testid={`${testId}-preview`}
          onError={() => setBroken(true)}
        />
      ) : src && broken ? (
        <p
          className="flex items-center gap-2 text-xs text-muted-foreground"
          data-testid={`${testId}-broken`}
        >
          <ImageOff className="h-4 w-4 shrink-0" />
          The current image could not be loaded. Upload a new one to replace it.
        </p>
      ) : null}

      <FileInput
        ref={input}
        id={id}
        testId={testId}
        accept={ENTITY_IMAGE_TYPES.join(",")}
        disabled={disabled}
        onChange={(e) => {
          // The input must only ever name the file the form holds. After a
          // refused file, or a picker cancelled in a browser that clears the
          // selection on cancel, it goes back to that file, or is emptied
          // when there is none, which also lets the same file be picked
          // again once it is fixed.
          const showHeldFile = () => {
            e.target.value = "";
            if (file && typeof DataTransfer !== "undefined") {
              const kept = new DataTransfer();
              kept.items.add(file);
              e.target.files = kept.files;
            }
          };
          const picked = e.target.files?.[0];
          if (!picked) {
            showHeldFile();
            return;
          }
          const refused = entityImageProblem(picked, maxBytes);
          setProblem(refused);
          if (refused) {
            showHeldFile();
            return;
          }
          onFile(picked);
        }}
      />

      {file && (
        <p
          className="truncate text-xs text-muted-foreground"
          data-testid={`${testId}-pending`}
        >
          {file.name} is uploaded when you save.
        </p>
      )}

      {problem && (
        <p
          role="alert"
          className="text-xs text-destructive-text"
          data-testid={`${testId}-problem`}
        >
          {problem}
        </p>
      )}

      {(undoLabel || (!required && src)) && (
        <div className="flex flex-wrap gap-2">
          {!required && src && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="max-sm:h-11"
              disabled={disabled}
              data-testid={`${testId}-remove`}
              onClick={() => {
                setProblem(null);
                onRemove();
              }}
            >
              <Trash2 className="h-4 w-4" />
              Remove image
            </Button>
          )}
          {undoLabel && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="max-sm:h-11"
              disabled={disabled}
              data-testid={`${testId}-undo`}
              onClick={() => {
                setProblem(null);
                onUndo();
              }}
            >
              <Undo2 className="h-4 w-4" />
              {undoLabel}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
